/**
 * A1 — the landing page, and the hardest requirement in the brief:
 *
 *   a first-time visitor on a 360px phone pastes a URL and taps one button
 *   without scrolling, reading, or dismissing anything.
 *
 * Everything this module adds is therefore either above the fold and part of
 * that action, or it is deferred. In particular the platform list is fetched
 * after load and populates a badge that appears in space already reserved for
 * it — a badge that pushes the button below the fold when it appears would
 * break the rule at the exact moment the user is reaching for it.
 */

import { ApiError, submitUrl } from '../lib/api.ts';
import type { SubmitResponse } from '../lib/api-types.ts';
import { pasteInto } from '../lib/clipboard.ts';
import { data, must, on, qs, setData } from '../lib/dom.ts';
import { withDownload } from '../lib/download-pref.ts';
import { mountExamples } from '../lib/examples.ts';
import { pickedLang, pickedPlatform, withThen } from '../lib/hand-off.ts';
import { track } from '../lib/events.ts';
import { t } from '../lib/i18n.ts';
import { detect, labelFor, loadPlatforms, nounFor } from '../lib/platforms.ts';
import { applyQuota, showLimitDialog } from '../lib/quota.ts';
import { mountTotals } from '../lib/totals.ts';
import { countTranslateStart, translateTarget, withAsked } from '../lib/translate-target.ts';
import { TOOL_SLUG } from '../lib/usage.ts';
import {
  appendNotice,
  clearState,
  plausibleHost,
  renderState,
  showProblem,
  showState,
} from '../lib/states.ts';

/** When the busy button changes to its slower wording. Most links answer well inside it. */
const SLOW_AFTER_MS = 8_000;

/**
 * `type="url"` gives the better mobile keyboard, and its native validation
 * rejects `youtube.com/watch?v=…` — which is what people actually paste. The
 * form is `novalidate` and the scheme is added here instead, once, before
 * anything else looks at the value.
 */
export function normalizeUrl(raw: string): string {
  const text = raw.trim();
  if (text === '') return '';
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`;
}

/**
 * Whether the field holds something shaped like a web address, after
 * `normalizeUrl`. Adding the scheme to anything sent "not a link at all" to
 * the server, which answered "We can't transcribe links from not a link at
 * all." (live verification). The bar is a host with a dot and no spaces;
 * everything past it is the server's to judge.
 */
export function looksLikeLink(raw: string): boolean {
  const url = normalizeUrl(raw);
  if (url === '') return false;
  try {
    return plausibleHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

/**
 * The longest link `?url=` may put in the box. A video's address is a few dozen characters; one of
 * thousands is not a video link, and the box is no place to display it.
 */
export const PREFILL_MAX_LENGTH = 2048;

/**
 * The link a `?url=` query asks the paste box to start with, or null when there is none worth
 * showing (SEO_BRIEF_06 §18.2: the browser extension opens `/youtube-transcript?url=<video>`).
 *
 * Read in the browser from `location.search`, never by the server, so a platform page is one
 * page whatever link it is opened with: the HTML does not change and the edge caches one copy
 * (deploy/cloudflare-worker/worker.js also leaves `url` out of what it caches under).
 *
 * Only an absolute http or https address with a plausible host is taken. `javascript:`, `data:`
 * and the rest are dropped, and so is an address carrying a user name or password, which no video
 * link has. The box is filled and nothing else happens: the visitor presses the button.
 */
export function prefillFrom(search: string): string | null {
  const raw = new URLSearchParams(search).get('url');
  if (raw === null) return null;
  const text = raw.trim();
  if (text === '' || text.length > PREFILL_MAX_LENGTH) return null;
  let parsed: URL;
  try {
    parsed = new URL(text);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  if (parsed.username !== '' || parsed.password !== '') return null;
  if (!plausibleHost(parsed.hostname)) return null;
  return parsed.href;
}

/**
 * The tool a link's translation is counted under (lib/usage.ts): the form's `data-usage-tool`,
 * which a translated page sets to its English page's slug (/es/traducir-video-de-youtube counts
 * as translate-youtube-video, /es/traductor-de-videos as video-translator), else the page's own
 * one-segment path, else `home`. A two-segment path fails TOOL_SLUG, so without the attribute
 * every translated page's links were the homepage's.
 */
export function usageTool(named: string | null | undefined, pathname: string): string {
  if (typeof named === 'string' && TOOL_SLUG.test(named)) return named;
  const slug = pathname.replace(/^\/+|\/+$/g, '');
  return TOOL_SLUG.test(slug) ? slug : 'home';
}

export function mountLandingPage(): void {
  // The homepage's running totals, under the card (lib/totals.ts). Hidden until they arrive,
  // and only the homepage has the strip.
  const totals = qs<HTMLElement>('[data-totals]');
  if (totals) void mountTotals(totals);
  // The homepage's "Try an example" buttons, under the card (lib/examples.ts): a stored sample
  // each, so no job starts. Only the homepage has them.
  const examples = qs<HTMLElement>('[data-examples]');
  if (examples) mountExamples(examples);

  const form = qs<HTMLFormElement>('[data-transcribe-form]');
  if (!form) return;

  const input = must<HTMLInputElement>('[data-url-input]', form);
  const submit = must<HTMLButtonElement>('[data-submit]', form);
  const pasteButton = qs<HTMLButtonElement>('[data-paste]', form);
  const badge = qs<HTMLElement>('[data-platform-badge]', form);
  const hint = qs<HTMLElement>('[data-caption-hint]', form);
  // The homepage's one region, or, where the paste box sits on a tool page above its drop zone
  // (/video-to-text), the region under the box, so a link's answer lands where the visitor is.
  const region = qs<HTMLElement>('#link-banner-region') ?? must<HTMLElement>('#banner-region');
  const pasteHint = qs<HTMLElement>('[data-paste-hint]', form);
  // The download a tool page promises (`data-download="txt"` on /video-to-text), carried on to
  // the job and transcript pages as `?download=`. Absent on the homepage, whose URLs stay as
  // they were.
  const download = data(form, 'download');
  // What the transcript page does next on the summarizers and the caption generator (`data-then`,
  // SEO_BRIEF_09 6.1 and 6.1b), carried on as `?do=` with the platform and the language picked
  // beside the box (lib/hand-off.ts). Absent everywhere else.
  const then = data(form, 'then');
  // The English page's slug on a translated page (`data-usage-tool`), for `usageTool`.
  const named = data(form, 'usage-tool');

  const busyLabel = data(submit, 'busy-label') ?? submit.textContent ?? '';
  // A relayed YouTube check can take 15 s or more, and a button that says the
  // same thing for that long reads as stuck.
  const slowLabel = data(submit, 'busy-slow-label') ?? busyLabel;
  let slowTimer: ReturnType<typeof setTimeout> | null = null;
  const idleLabel = submit.textContent ?? '';
  let inFlight: AbortController | null = null;

  /* ── platform detection ─────────────────────────────────────────────── */

  function paintDetection(): void {
    const value = input.value;
    const found = detect(normalizeUrl(value));
    if (badge) {
      badge.hidden = found.kind !== 'platform';
      if (found.kind === 'platform') badge.textContent = found.label;
    }
    if (hint) {
      hint.replaceChildren();
      hint.hidden = true;
    }
    clearState(region);

    if (found.kind === 'unsupported' || found.kind === 'podcast_app') {
      // Caught client-side where possible, always re-checked server-side.
      if (found.kind === 'podcast_app') showState(region, 'E1_PODCAST_APP', { app: found.app });
      else showState(region, 'E1', { host: found.host });
      input.setAttribute('aria-invalid', 'true');
      return;
    }
    input.setAttribute('aria-invalid', 'false');

    if (found.kind === 'platform') {
      if (found.platform.health !== 'ok') {
        showState(region, 'E20', {
          platform: found.platform.label,
          platform_2: found.platform.label,
        });
        return;
      }
      // The caption-path promise, made at paste time because it is true and
      // because it is the cheapest traffic there is.
      if (found.platform.has_caption_path && hint) {
        const frag = renderState('A1_CAPTION_HINT', { platform_label: found.platform.label });
        if (frag) {
          hint.append(frag);
          hint.hidden = false;
        }
      }
    }
  }

  on(input, 'input', paintDetection);
  on(input, 'change', paintDetection);

  /* ── paste ──────────────────────────────────────────────────────────── */

  if (pasteButton) {
    on(pasteButton, 'click', () => {
      // Not disabled, no spinner: in Safari the read can sit unsettled behind
      // the OS paste callout, and a disabled button there is precisely the
      // dead control this is meant to avoid.
      void pasteInto(input).then((outcome) => {
        if (outcome === 'pasted') {
          paintDetection();
          if (pasteHint) pasteHint.hidden = true;
        } else if (pasteHint) {
          // The clipboard read was refused or is unavailable — including in
          // the in-app browsers that report support and then fail. The field
          // is focused and selected; this line says what to do next.
          pasteHint.hidden = false;
        }
      });
    });
  }

  /* ── submit ─────────────────────────────────────────────────────────── */

  function setBusy(busy: boolean): void {
    setData(submit, 'busy', busy ? 'true' : 'false');
    submit.disabled = busy;
    submit.textContent = busy ? busyLabel : idleLabel;
    if (slowTimer !== null) clearTimeout(slowTimer);
    slowTimer = busy
      ? setTimeout(() => {
          submit.textContent = slowLabel;
        }, SLOW_AFTER_MS)
      : null;
  }

  function handleResolved(response: SubmitResponse): void {
    applyQuota({
      asr_seconds_remaining: response.quota.asr_seconds_remaining,
      asr_seconds_budget: response.quota.asr_seconds_budget,
      requests_remaining: response.quota.requests_remaining,
      requests_budget: response.quota.requests_remaining,
      resets_in_s: response.quota.resets_in_s,
      running_job: null,
    });

    for (const notice of response.notices) {
      if (notice.code === 'SERVED_FROM_CACHE') track('cache_hit', {});
      if (notice.code === 'SERVED_FROM_CAPTIONS') {
        const found = detect(normalizeUrl(input.value));
        track('caption_hit', {
          platform: found.kind === 'platform' ? found.platform.platform : 'unknown',
        });
      }
      if (notice.code === 'ASR_FALLBACK_NO_CAPTIONS') track('youtube_no_captions', {});
      appendNotice(region, notice);
    }

    // A link page that translates (SEO_BRIEF_09 5.1): counted under the page's own slug, the
    // English page's on a translated page (`usageTool`). The job page counts the done under it.
    if (response.translate) {
      const path = typeof location.pathname === 'string' ? location.pathname : '';
      countTranslateStart(
        response.translate,
        usageTool(named, path),
        'video',
        'link',
        response.resolved ? undefined : response.job_id,
      );
    }

    // A 202 with duration_s null means quota has NOT been charged yet, so the
    // meter must not decrement on it (contracts/api.md §3.2). Nothing above
    // decrements — the meter is repainted from the server's own numbers, which
    // is the only way that stays true.
    location.assign(
      withThen(
        withDownload(
          response.resolved
            ? withAsked(response.transcript_url, response.translate)
            : response.job_url,
          download,
        ),
        then,
        pickedPlatform(),
        pickedLang(),
      ),
    );
  }

  on(form, 'submit', (ev) => {
    ev.preventDefault();
    const url = normalizeUrl(input.value);
    if (url === '') {
      input.focus();
      return;
    }
    if (!looksLikeLink(input.value)) {
      // Refused here, before a request: the server would only say it cannot
      // transcribe links from a host made of the visitor's sentence.
      showState(region, 'E1_NOT_A_LINK');
      track('error_state', { state: 'E1_NOT_A_LINK' });
      input.setAttribute('aria-invalid', 'true');
      return;
    }
    inFlight?.abort();
    inFlight = new AbortController();
    clearState(region);
    setBusy(true);

    // The target picker's language on a page that translates (SEO_BRIEF_09 5.1); none elsewhere.
    void submitUrl(url, inFlight.signal, translateTarget())
      .then(handleResolved)
      .catch(async (err: unknown) => {
        if (!(err instanceof ApiError)) throw err;
        const problem = err.problem;
        // E1's platform variant needs the noun from GET /api/platforms. It is
        // almost always here by now; when it is not, waiting for it (it never
        // rejects) beats falling back to the E1 that contradicts itself.
        const e1Platform = problem.state === 'E1' ? problem.url?.platform : null;
        if (e1Platform) await loadPlatforms();
        const found = detect(url);
        const platformLabel =
          found.kind === 'platform'
            ? found.platform.label
            : problem.platform
              ? labelFor(problem.platform.platform)
              : '';
        // E10 is a dialog, not a banner — dismissible, never a wall.
        if (problem.code === 'QUOTA_EXHAUSTED') {
          showLimitDialog({
            budgetSeconds: problem.quota?.asr_seconds_budget,
            resetsInS: problem.quota?.resets_in_s ?? null,
          });
          track('error_state', { state: 'E10' });
          return;
        }
        showProblem(region, problem, {
          platform: platformLabel,
          platform_2: platformLabel,
          // The API's noun is English; a translated page says it in its own language.
          noun: e1Platform ? t(nounFor(e1Platform)) : '',
        });
      })
      .finally(() => {
        setBusy(false);
        inFlight = null;
      });
  });

  /* ── ?url= ──────────────────────────────────────────────────────────── */

  // A link handed over in the address (the browser extension's button) goes in the box, and the
  // badge and caption line paint as if it had been pasted. Never submitted from here. A box the
  // browser already refilled (back to a page with text in it) keeps what the visitor typed.
  const prefill = prefillFrom(location.search);
  if (prefill !== null && input.value.trim() === '') {
    input.value = prefill;
    paintDetection();
  }

  // Platform data arrives after load, by contract: it carries live health and
  // the landing page is edge-cached for 24 h.
  void loadPlatforms().then(() => {
    if (input.value.trim() !== '') paintDetection();
  });
}
